import { Button, Drawer, InsightIcon } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { InsightDirection } from '@toggle/toggle';
import { isSameDay } from 'date-fns';
import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { useExploreStore } from '~/views/insights/hooks/use-explore-store/useExploreStore';

import { InsightsDrawerContent } from './components/insights-drawer-content/InsightsDrawerContent';
import * as S from './InsightsDrawer.styles';

export type InsightsDrawerProps = {
  insightId: string;
};

export const InsightsDrawer = ({ insightId }: InsightsDrawerProps) => {
  useDisableScroll();
  const { t } = useTranslation(['widget']);
  const { closeDrawer } = useInsightDrawer();
  const { data, isFetching } = useGetInsightWidgetData(insightId);

  const { selectedExploreInsight, setSelectedExploreInsight } = useExploreStore(
    state => ({
      selectedExploreInsight: state.selectedExploreInsight,
      setSelectedExploreInsight: state.setSelectedExploreInsight,
    })
  );
  // Temp: update to navigate from watchlist when available
  const { navigateToPage } = useNavigateToAssetOverview();

  useEffect(() => {
    return () => {
      setSelectedExploreInsight();
    };
  }, []);

  const navigateToAssetOverview = () => {
    if (data?.data?.entity.tag) {
      navigateToPage({ entityTag: data.data.entity.tag });
    }
  };

  const getDrawerContent = () => {
    if (isFetching) {
      return <S.LoadingSkeleton />;
    }

    if (data?.error || !data?.data) {
      return <ErrorMessageWrapper variant="empty" />;
    }

    let icon: ReactNode = null;

    if (selectedExploreInsight) {
      const conditions = getConditionIcons({
        conditions: selectedExploreInsight.conditions,
        kind: selectedExploreInsight.metadata.kind,
      });
      const isCombined = conditions.length > 1;
      const isNewInsight = isSameDay(
        new Date(selectedExploreInsight.created_at),
        new Date()
      );
      icon = (
        <InsightIconsRoot
          conditions={conditions}
          direction={selectedExploreInsight.direction}
          isStarred={isNewInsight}
          isCombined={isCombined}
        />
      );
    } else {
      const isBullish =
        data.data.insight.metadata.direction === InsightDirection.Bullish;
      const showSparkles = isSameDay(
        new Date(
          data.data.insight.metadata?.last_value_date ??
            data.data.insight.created_at
        ),
        new Date()
      );
      icon = (
        <InsightIcon
          isBullish={isBullish}
          showSparkles={showSparkles}
          options={{ iconSize: 32 }}
        />
      );
    }

    return (
      <InsightsDrawerContent
        insightsData={data.data}
        header={() => (
          <S.Header>
            {icon}
            <S.Title>
              {data.data.insight?.simple_title ?? data.data.insight.card.name}
            </S.Title>
          </S.Header>
        )}
        showViewMore
      />
    );
  };

  return (
    <Drawer
      data-testid="insights-drawer"
      isOpen
      handleCloseClick={closeDrawer}
      headerButton={
        <Button
          iconName="Expand"
          label={t('widget:assetOverview.assetOverview')}
          onClick={navigateToAssetOverview}
          variant="empty"
          size="xsmall"
        />
      }
    >
      <WithFooterWrapper
        trackingMetadata={{
          source: POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER,
        }}
      >
        {getDrawerContent()}
      </WithFooterWrapper>
    </Drawer>
  );
};
